import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { Suspense, useMemo, useState } from 'react';
import { useHostEffectiveAccessQuery } from '#api/hosts.queries.ts';
import type { GetHostsFilter } from '#api/hosts.ts';
import { makeRouteQueryOptions } from '#api/routes.queries.ts';
import { AlertIcon, InfoCircle } from '#assets/ReactSvg.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { InlineButton } from '#components/atoms/buttons/inline-button/InlineButton.tsx';
import { DetailCard } from '#components/atoms/cards/detail-card/DetailCard.tsx';
import { Checkbox } from '#components/atoms/forms/checkbox/Checkbox.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { Pill } from '#components/atoms/pill/Pill.tsx';
import { TagPill } from '#components/atoms/pill/tag-pill/TagPill.tsx';
import { HostsCountLink } from '#components/patterns/entity-links/hosts-count-link/HostsCountLink.tsx';
import { TagLink } from '#components/patterns/entity-links/tag-link/TagLink.tsx';
import { RoleName } from '#components/patterns/role-name/RoleName.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import { InlineTagsList } from '#components/patterns/tags/InlineTagsList.tsx';
import type { AccessTarget, EffectiveAccessEntry, RouteTargetStatus } from '#dn-types/effective-access.ts';
import type { Host } from '#dn-types/host.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useMatchMedia } from '#hooks/useMatchMedia.ts';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { stringifyPortRange } from '#screens/roles/detail/stringify-port-range/stringifyPortRange.ts';
import styles from './EffectiveAccessCard.module.css';

type Props = {
  // networkID scopes each target's member count and link to peers this host can actually reach;
  // see targetHostsFilter for why the card picks that reading.
  host: Pick<Host, 'id' | 'networkID'>;
  className?: string;
};

/** Entries whose target resolves to a set of hosts, as opposed to a routed CIDR. */
type HostAccessEntry = EffectiveAccessEntry & { target: Extract<AccessTarget, { type: 'role' | 'tag' }> };
/**
 * `localCIDR` is optional across the union but the API always sets it on a route target, so the
 * route variant requires it and the CIDR cell can't silently render blank.
 */
type RouteAccessEntry = EffectiveAccessEntry & {
  target: Extract<AccessTarget, { type: 'route' }>;
  localCIDR: string;
};

const HEADING = 'Effective outbound access';

/**
 * Lists what a host may reach: every firewall rule in the organization whose source predicate the
 * host satisfies, paired with the role, tag, or route that rule protects. The inverse of the inbound
 * rules shown by {@link FirewallCard}.
 */
export function EffectiveAccessCard({ host, className }: Props) {
  const { data, error, isPending, refetch } = useHostEffectiveAccessQuery(host.id);
  const [showICMP, setShowICMP] = useState(false);

  if (error) {
    return (
      <DetailCard className={cn(styles.EffectiveAccessCard, className)}>
        <h2 className="h4 mb-16">{HEADING}</h2>
        {/* The rest of the page recovered on its own, so the card offers its own retry rather
            than making a working page reload to refresh one query. */}
        <p className="p-md mb-0">
          <AlertIcon /> Problem retrieving outbound access.{' '}
          <InlineButton onClick={() => void refetch()}>Try again</InlineButton>
        </p>
      </DetailCard>
    );
  }

  if (isPending) {
    return (
      <DetailCard className={cn(styles.EffectiveAccessCard, className)}>
        <LoadingSpinner />
      </DetailCard>
    );
  }

  const entries = data.data;
  const hostEntries = entries.filter((entry): entry is HostAccessEntry => entry.target.type !== 'route');
  const routeEntries = entries.filter((entry): entry is RouteAccessEntry => entry.target.type === 'route');
  const icmpCount = hostEntries.filter((entry) => entry.protocol === 'ICMP').length;

  return (
    <DetailCard className={cn(styles.EffectiveAccessCard, className)}>
      <div>
        <h2 className="h4 mb-8">{HEADING}</h2>
        <p className="p-md mb-16">
          Where this host is allowed to connect, granted by firewall rules on roles, tags, and routes. Click a role,
          tag, or route to make changes.
        </p>
        {/* Fleets converge on a per-role "allow ping from anywhere" rule, so ICMP rows can outnumber
            the grants worth auditing - they start hidden, with the labelled count keeping what is
            held back discoverable. A view filter only: the banners keep reading the full data,
            since they diagnose configuration rather than what is on screen. It covers only the
            host table, where the ping convention piles up; route rows are few and carry status and
            CIDR diagnostics worth keeping in view, so the count reflects host rows alone. */}
        {icmpCount ?
          <Checkbox
            className="mb-16"
            checked={showICMP}
            onChange={(event) => setShowICMP(event.target.checked)}
            label={`Show ICMP rules (${icmpCount})`}
          />
        : null}
        {/* RoleName and InlineTagsList read the max roles/tags lists, which the detail page has
            already prefetched; the boundary keeps a cold cache from suspending the whole page. */}
        <Suspense fallback={<LoadingSpinner />}>
          <HostAccessTable entries={hostEntries} networkID={host.networkID} hideICMP={!showICMP} />

          {routeEntries.length ?
            <div className={styles.EffectiveAccessCard_routes}>
              <h3 className="h5 mb-16">Route access</h3>
              <RouteAccessTable entries={routeEntries} />
            </div>
          : null}
        </Suspense>
      </div>
    </DetailCard>
  );
}

/**
 * Renders the set of hosts a rule admits — this host among them, which is why the row is listed.
 * Naming the whole set rather than this host's matching role/tags answers the question an audit
 * asks: a grant reading "All hosts" is org-wide, so only the rule can revoke it, while a narrower
 * one can also be dropped by changing this host. Same field and rendering as the inbound table's
 * "Allowed hosts" column.
 */
function GrantedTo({ entry }: { entry: EffectiveAccessEntry }) {
  const { allowedRoleID } = entry;
  const tags = entry.allowedTags ?? [];

  return (
    <div className="flex align-baseline nowrap gap-8">
      {allowedRoleID ?
        <span className="flex gap-4 mw-0">
          <RoleName className="inline truncate padr-0" roleID={allowedRoleID} />
          {`hosts ${tags.length ? 'tagged ' : ''}`}
        </span>
      : `All hosts ${tags.length ? 'tagged ' : ''}`}
      {tags.length ?
        <InlineTagsList tagNames={tags} truncateAfter={1} />
      : null}
    </div>
  );
}

/**
 * The hosts making up a target. The API deliberately returns no count, leaving what "members" means
 * to whoever asks, so this picks the reading the card is about: roles and tags span the
 * organization, but a host only reaches peers sharing its network, so counting org-wide would
 * promise reach this host does not have.
 */
function targetHostsFilter(target: HostAccessEntry['target'], networkID: string): GetHostsFilter {
  return target.type === 'role' ? { roleID: target.role.roleID, networkID } : { tags: [target.tag.tag], networkID };
}

/**
 * Why a subscribed route carries no traffic. Both cases are silent misconfigurations: the grant is
 * real, but the route never reaches this host's config, so the CIDRs stay unreachable.
 */
const ROUTE_STATUS_LABELS: Record<Exclude<RouteTargetStatus, 'OK'>, string> = {
  NO_ROUTER_HOST: 'No router host',
  ROUTER_NETWORK_MISMATCH: 'Router in another network',
};

/**
 * Names why a rule clamps to nothing. The effective-access payload cannot say: an empty result
 * looks the same whether the route routes no CIDRs at all or the rule misses every CIDR it does
 * route, so the route detail is fetched to tell the two apart. Until it answers - or if it never
 * does - a generic label keeps the warning on screen: naming the cause is worth a request, losing
 * the warning is not.
 */
function DeadRulePill({ routeID }: { routeID: string }) {
  const { data } = useQuery(makeRouteQueryOptions(routeID));
  const label =
    data === undefined ? 'Opens nothing'
    : Object.keys(data.data.routableCIDRs).length ? 'No routable CIDRs match'
    : 'Route has no routable CIDRs';

  return (
    <Pill variant="alert" Icon={AlertIcon}>
      {label}
    </Pill>
  );
}

/** A rule open to every host on any protocol and port, restricting nothing about the source. */
function isUnconditional(entry: EffectiveAccessEntry) {
  return (
    entry.protocol === 'ANY' &&
    entry.portRange === null &&
    entry.allowedRoleID === null &&
    !(entry.allowedTags ?? []).length
  );
}

/** Identity of the thing a grant points at, so rules on the same destination group together. */
function targetKey(target: HostAccessEntry['target']) {
  return target.type === 'role' ? `role:${target.role.roleID}` : `tag:${target.tag.tag}`;
}

/**
 * Whether any single target is reached by an unconditional rule alongside narrower ones. The
 * domination is per-target: an allow-all on one role says nothing about a rule on a different role,
 * since the two grant access to disjoint sets of hosts. Only within one target does the broad rule
 * make its neighbours redundant, which is why the check groups before testing.
 */
function hasDominatedTarget(entries: HostAccessEntry[]) {
  const byTarget = new Map<string, HostAccessEntry[]>();
  for (const entry of entries) {
    const key = targetKey(entry.target);
    const group = byTarget.get(key);
    if (group) {
      group.push(entry);
    } else {
      byTarget.set(key, [entry]);
    }
  }

  return [...byTarget.values()].some((group) => group.length >= 2 && group.some((entry) => isUnconditional(entry)));
}

const hostColumnHelper = createColumnHelper<HostAccessEntry>();

function HostAccessTable({
  entries,
  networkID,
  hideICMP,
}: {
  entries: HostAccessEntry[];
  networkID: string;
  hideICMP: boolean;
}) {
  // Derived narrow threshold: this page wraps the card in
  // 312px of chrome above --size-breakpoint-s (200 sidebar + 2x40 body padding + 2x16 card
  // padding) and 64px below it. The full table sums to 280 + 80 + 100 + 150 + 260 = 870, which
  // stops fitting below 870 + 312 = 1182 of viewport. The narrow tier shrinks the two prose
  // columns (230 can reach, 210 granted to) so every column stays on screen down to 770 + 312 =
  // 1082, and to 770 + 64 = 834 once the sidebar collapses; below that the card scrolls sideways.
  // Columns are never hidden: a rule row only reads as a rule with source, destination, and ports
  // all on show.
  const narrow = useMatchMedia('(max-width: 1181px)');
  const location = useAppLocation();

  const columns = useMemo(
    () => [
      hostColumnHelper.display({
        id: 'target',
        header: 'Can reach',
        size: narrow ? 230 : 280,
        cell(info) {
          const { target } = info.row.original;
          return (
            <div className="flex align-baseline nowrap gap-8">
              {target.type === 'role' ?
                <span className="flex gap-4 mw-0 nowrap">
                  Role:
                  <Link className="a inline truncate" to={`/roles/${target.role.roleID}`} state={{ from: location }}>
                    {target.name}
                  </Link>
                </span>
              : <TagLink className={styles.EffectiveAccessCard_tagLink} tagName={target.tag.tag}>
                  <TagPill label={target.tag.tag} />
                </TagLink>
              }
              <HostsCountLink filter={targetHostsFilter(target, networkID)} />
            </div>
          );
        },
      }),
      hostColumnHelper.accessor('protocol', {
        header: 'Protocol',
        size: 80,
        cell: (info) => (info.getValue() === 'ANY' ? 'Any' : info.getValue()),
      }),
      hostColumnHelper.accessor('portRange', {
        header: 'Port range',
        size: 100,
        cell: (info) => stringifyPortRange(info.getValue()),
      }),
      hostColumnHelper.accessor('description', {
        header: 'Description',
        // The fill column: free text soaks up whatever width the sized columns leave over, and
        // gives it back first when space runs out; the size is only its floor.
        size: 150,
        meta: { truncate: true, fill: true },
      }),
      hostColumnHelper.display({
        id: 'granted-to',
        header: 'Granted to',
        size: narrow ? 210 : 260,
        cell: (info) => <GrantedTo entry={info.row.original} />,
      }),
    ],
    [networkID, narrow, location]
  );

  if (!entries.length) {
    return (
      <Banner variant="info" Icon={InfoCircle} className="mb-0">
        No firewall rules grant this host access to other hosts, so it can&rsquo;t open connections to them.
      </Banner>
    );
  }

  const visibleEntries = hideICMP ? entries.filter((entry) => entry.protocol !== 'ICMP') : entries;

  // Distinct from the no-access banner above: rules exist, the filter is just hiding all of them -
  // and since ICMP starts hidden, an all-ICMP host lands here without ever touching the filter, so
  // the note names the checkbox that brings the rows back. An unconditional rule is protocol ANY
  // and so never filtered, meaning nothing dominated can be on show here either; skipping the
  // banners is safe, not just tidy.
  if (!visibleEntries.length) {
    return (
      <Banner variant="info" Icon={InfoCircle} className="mb-0">
        Every rule granting host access is an ICMP rule. Check &ldquo;Show ICMP rules&rdquo; above to see them.
      </Banner>
    );
  }

  return (
    <>
      {hasDominatedTarget(entries) ?
        <Banner variant="info" Icon={InfoCircle} className="mb-24">
          One of the targets below is reached by a rule allowing all hosts on any protocol and port. Because that rule
          permits all traffic, the narrower rules on that same target have no effect.
        </Banner>
      : null}
      <div className={styles.EffectiveAccessCard_table}>
        <Table
          data={visibleEntries}
          columns={columns}
          caption="Hosts this host is allowed to connect to"
          fixedLayout={true}
        />
      </div>
    </>
  );
}

const routeColumnHelper = createColumnHelper<RouteAccessEntry>();

function RouteAccessTable({ entries }: { entries: RouteAccessEntry[] }) {
  const location = useAppLocation();
  const columns = useMemo(
    () => [
      routeColumnHelper.display({
        id: 'cidr',
        header: 'CIDR',
        size: 220,
        meta: { truncate: true },
        cell(info) {
          const { localCIDR, effectiveCIDRs, target } = info.row.original;

          // Without a clamped reach the rule applies as written, so the stored CIDR is both what
          // nebula enforces and what identifies the rule. Nothing to reconcile.
          if (!effectiveCIDRs) {
            return <span className={styles.EffectiveAccessCard_cidr}>{localCIDR}</span>;
          }

          // Clamping to nothing leaves a grant that emits no nebula rule at all — real on paper,
          // dead in the config. A blank cell would read as "unrestricted", the opposite of true.
          // The stored CIDR still shows, since it is the only way to find the offending rule.
          //
          // Otherwise the clamped destinations are the reach, and the stored CIDR rides along
          // whenever it is not already the value on show: the routes page lists rules by what was
          // typed, so without it an admin cannot tell which rule to open to change any of this.
          // One CIDR per line rather than a joined list, which would truncate into an ellipsis
          // this narrow column cannot recover.
          const storedIsRedundant = effectiveCIDRs.length === 1 && effectiveCIDRs[0] === localCIDR;

          return (
            <div className={styles.EffectiveAccessCard_cidrCell}>
              {effectiveCIDRs.length ?
                effectiveCIDRs.map((cidr) => (
                  <span key={cidr} className={cn('truncate', styles.EffectiveAccessCard_cidr)}>
                    {cidr}
                  </span>
                ))
              : <DeadRulePill routeID={target.route.routeID} />}
              {storedIsRedundant ? null : (
                <span className="p-sm mb-0 truncate">
                  Rule: <span className={styles.EffectiveAccessCard_cidr}>{localCIDR}</span>
                </span>
              )}
            </div>
          );
        },
      }),
      routeColumnHelper.display({
        id: 'route',
        header: 'Via route',
        size: 220,
        meta: { truncate: true },
        cell(info) {
          const { target } = info.row.original;
          // A broken route still lists its grant, so the reason it carries nothing rides next to
          // the route name rather than in a column that would read "OK" on nearly every row.
          const status = target.route.status === 'OK' ? null : ROUTE_STATUS_LABELS[target.route.status];
          return (
            <div className="flex align-baseline nowrap gap-8">
              <Link className="a inline truncate" to={`/routes/${target.route.routeID}`} state={{ from: location }}>
                {target.name}
              </Link>
              {status ?
                <Pill variant="alert" Icon={AlertIcon}>
                  {status}
                </Pill>
              : null}
            </div>
          );
        },
      }),
      routeColumnHelper.accessor('protocol', {
        header: 'Protocol',
        size: 80,
        cell: (info) => (info.getValue() === 'ANY' ? 'Any' : info.getValue()),
      }),
      routeColumnHelper.accessor('portRange', {
        header: 'Port range',
        size: 100,
        cell: (info) => stringifyPortRange(info.getValue()),
      }),
      routeColumnHelper.accessor('description', {
        header: 'Description',
        // The fill column: free text soaks up whatever width the sized columns leave over, and
        // gives it back first when space runs out; the size is only its floor.
        size: 150,
        meta: { truncate: true, fill: true },
      }),
      routeColumnHelper.display({
        id: 'granted-to',
        header: 'Granted to',
        size: 240,
        cell: (info) => <GrantedTo entry={info.row.original} />,
      }),
    ],
    [location]
  );

  const brokenCount = entries.filter((entry) => entry.target.route.status !== 'OK').length;
  // The pill locates a dead rule; this explains it, matching the broken-route treatment. Only a
  // rule clamped to nothing counts: an absent effectiveCIDRs is an unclamped rule, which applies as
  // written.
  const deadRuleCount = entries.filter((entry) => entry.effectiveCIDRs?.length === 0).length;

  return (
    <>
      {brokenCount ?
        <Banner variant="alert" Icon={AlertIcon} className="mb-24">
          {brokenCount === 1 ? 'One of these routes is' : `${brokenCount} of these routes are`} subscribed but not
          installed on this host, so the firewall permits the traffic and nothing carries it. Fix the route&rsquo;s
          router host to make the access effective.
        </Banner>
      : null}
      {deadRuleCount ?
        <Banner variant="alert" Icon={AlertIcon} className="mb-24">
          {deadRuleCount === 1 ? 'One rule below opens' : `${deadRuleCount} rules below open`} no CIDRs: the
          rule&rsquo;s local CIDR doesn&rsquo;t overlap anything the route actually routes. Edit the rule&rsquo;s CIDR,
          or the route&rsquo;s routable CIDRs, to make the grant effective.
        </Banner>
      : null}
      {/* No narrow tier here: even shaved, six columns exceed any viewport the sidebar-collapsed
          layout offers, and route rows carry mono CIDR stacks that need their width. The card's
          horizontal scroll covers what does not fit. */}
      <div className={cn(styles.EffectiveAccessCard_table, styles.EffectiveAccessCard_routeTable)}>
        <Table
          data={entries}
          columns={columns}
          caption="Routed CIDRs this host is allowed to reach"
          fixedLayout={true}
        />
      </div>
    </>
  );
}
